// Phase 5 - Reverse lookup: legal description text -> GeoJSON Feature(s).
// Supports aliquot (PLSS) descriptions across ALL PLSS states, including
// eastern states like Illinois where BLM's CadNSDI only carries section-level
// polygons (we split those client-side with turf when a smaller aliquot is
// requested).

import { bbox as turfBbox, bboxPolygon, intersect as turfIntersect } from '@turf/turf';
import { walkMetesAndBounds, cornerOfBbox } from './metesWalker.js';

const CLAUDE_PROXY = '/.netlify/functions/claude';
const PLSS_PROXY = '/.netlify/functions/plss';

const MERIDIAN_CODE_BY_NAME = {
  '1st': '01', 'first': '01',
  '2nd': '02', 'second': '02',
  '3rd': '03', 'third': '03',
  '4th': '04', 'fourth': '04',
  '5th': '05', 'fifth': '05',
  '6th': '06', 'sixth': '06',
  'black hills': '07',
  'boise': '08', 'chickasaw': '09', 'choctaw': '10', 'cimarron': '11',
  'copper river': '12', 'fairbanks': '13', 'gila and salt river': '14',
  'gila-salt river': '14', 'humboldt': '15', 'huntsville': '16', 'indian': '17',
  'kateel river': '18', 'louisiana': '19', 'michigan': '20',
  'mount diablo': '21', 'navajo': '22', 'new mexico': '23',
  'principal': '24', 'st. helena': '25', 'saint helena': '25',
  'st. stephens': '26', 'saint stephens': '26', 'salt lake': '27',
  'san bernardino': '28', 'seward': '29', 'tallahassee': '30',
  'uintah special': '31', 'umiat': '32', 'ute': '33',
  'washington': '34', 'willamette': '35', 'wind river': '36',
};

export async function reverseLookup(descriptionText) {
  if (!descriptionText || !descriptionText.trim()) throw new Error('Empty description');
  const parsed = await parseWithClaude(descriptionText);
  if (!parsed || !parsed.sections || parsed.sections.length === 0) {
    throw new Error('Could not extract any PLSS sections from the description.');
  }
  const features = [];
  const metesFeatures = [];
  for (const section of parsed.sections) {
    if (!section.state && !section.meridian) {
      throw new Error(
        `Description for Section ${section.section||'?'} T${section.township||'?'}${section.township_dir||''} R${section.range||'?'}${section.range_dir||''} is missing a state and meridian. ` +
        `Township/range numbers repeat across meridians. Edit to include e.g. "3rd Principal Meridian, Illinois" and try again.`
      );
    }
    const parts = await resolveSectionAliquots(section);
    for (const f of parts) {
      if (f.properties == null) f.properties = {};
      f.properties.kind = 'aliquot_container';
      features.push(f);
    }

    // If a metes-and-bounds sub-tract was parsed, walk it from the commencement
    // anchor, resolving "of" to the correct sub-region (section / quarter / QQ).
    if (section.metes && Array.isArray(section.metes.calls) && section.metes.calls.length >= 3) {
      const sectionFeature = await fetchSectionFeature(section);
      const ref = sectionFeature || parts[0];
      let startCoord = null;
      if (ref) {
        const anchor = section.metes.commencement?.anchor || 'SE corner';
        const ofStr = section.metes.commencement?.of || 'section';
        const subBbox = bboxForAliquotRef(ref, ofStr);
        startCoord = subBbox ? cornerOfBbox(subBbox, anchor) : null;
      }
      if (ref) {
        const walked = walkMetesAndBounds(section.metes, ref, startCoord);
        if (walked) metesFeatures.push(walked);
      }
    }
  }
  for (const f of metesFeatures) features.push(f);
  if (features.length === 0) {
    throw new Error('Parsed the description but BLM returned no matching section for that state/meridian.');
  }
  return { type: 'FeatureCollection', features, meta: { parsed } };
}

async function resolveSectionAliquots(section) {
  // Layer 3 serves two purposes depending on the state:
  //   - Western states: QQ cells with SECDIVNO like "SWNE"
  //   - Eastern states (e.g. IL): section-level features with SECDIVNO=null
  const layer3 = await fetchCells(section, '3');

  // First try QQ cells
  const wantedCodes = expandAliquotToCodes(section.aliquot);
  const matchingQQ = filterCellsByCode(layer3, wantedCodes);
  if (matchingQQ.length > 0) return matchingQQ;

  // Fallback: find section-level features (SECDIVNO null/empty) on layer 3
  let sectionFeatures = layer3.filter((f) => {
    const code = (f.properties || {}).SECDIVNO;
    return !code;
  });

  // If nothing in layer 3 matches, try layer 1 (township) to at least zoom in.
  if (sectionFeatures.length === 0) {
    // Some states only have township-level polygons, not individual sections.
    const twnCells = await fetchCells(section, '1');
    if (twnCells.length === 0) return [];
    // Return the township polygon itself - less precise but at least in the right place.
    return twnCells.slice(0, 1);
  }

  const sectionFeature = sectionFeatures[0];
  if (aliquotIsWhole(section.aliquot)) return [sectionFeature];
  return splitSectionByAliquot(sectionFeature, section.aliquot);
}

function aliquotIsWhole(a) {
  return a === 'ALL' || (Array.isArray(a) && a.length === 1 && a[0] === 'ALL');
}

// Split a section polygon into aliquot pieces using its bbox midpoints.
// Approximation: PLSS sections aren't exactly rectangular, but for the purpose
// of user visualization, axis-aligned bbox quarter splits are close enough.
function splitSectionByAliquot(sectionFeature, aliquot) {
  const out = [];
  const parts = Array.isArray(aliquot) ? aliquot : [aliquot];
  for (const partRaw of parts) {
    const codes = expandOne(normalizeAliquot(String(partRaw)));
    for (const code of codes) {
      const rect = rectForCode(sectionFeature, code);
      if (!rect) continue;
      const inter = safeIntersect(sectionFeature, rect);
      if (inter) out.push(inter);
    }
  }
  return out;
}

// Given a section feature and a SECDIVNO-style 4-letter code (e.g. "SWNE"),
// return a Feature rectangle representing that QQ cell's portion of the section.
function rectForCode(sectionFeature, code) {
  const bb = turfBbox(sectionFeature); // [minX, minY, maxX, maxY]
  if (!bb || bb.some((v) => !isFinite(v))) return null;
  const [x0, y0, x1, y1] = bb;
  const mx = (x0 + x1) / 2;
  const my = (y0 + y1) / 2;
  // Quarter-section bbox by outer 2 chars (first split of section)
  const outer = code.slice(-2);
  let [qx0, qy0, qx1, qy1] = [x0, y0, x1, y1];
  if (outer === 'NE') [qx0, qy0, qx1, qy1] = [mx, my, x1, y1];
  else if (outer === 'NW') [qx0, qy0, qx1, qy1] = [x0, my, mx, y1];
  else if (outer === 'SE') [qx0, qy0, qx1, qy1] = [mx, y0, x1, my];
  else if (outer === 'SW') [qx0, qy0, qx1, qy1] = [x0, y0, mx, my];
  else return null;
  if (code.length === 2) {
    return bboxPolygon([qx0, qy0, qx1, qy1]);
  }
  // Split the quarter further by inner 2 chars
  const inner = code.slice(0, 2);
  const imx = (qx0 + qx1) / 2;
  const imy = (qy0 + qy1) / 2;
  let [ix0, iy0, ix1, iy1] = [qx0, qy0, qx1, qy1];
  if (inner === 'NE') [ix0, iy0, ix1, iy1] = [imx, imy, qx1, qy1];
  else if (inner === 'NW') [ix0, iy0, ix1, iy1] = [qx0, imy, imx, qy1];
  else if (inner === 'SE') [ix0, iy0, ix1, iy1] = [imx, qy0, qx1, imy];
  else if (inner === 'SW') [ix0, iy0, ix1, iy1] = [qx0, qy0, imx, imy];
  else return null;
  return bboxPolygon([ix0, iy0, ix1, iy1]);
}

function safeIntersect(a, b) {
  try { return turfIntersect({ type: 'FeatureCollection', features: [a, b] }); }
  catch { try { return turfIntersect(a, b); } catch { return null; } }
}

async function parseWithClaude(text) {
  const prompt = [
    'You are parsing a property legal description. Extract PLSS sections only.',
    '',
    'Return ONLY valid JSON (no markdown, no preamble) matching this schema:',
    '{',
    '  "sections": [',
    '    {',
    '      "aliquot": "ALL" | string[],',
    '      "section": string,',
    '      "township": string,',
    '      "township_dir": "N" | "S",',
    '      "range": string,',
    '      "range_dir": "E" | "W",',
    '      "state": string | null,',
    '      "meridian": string | null,',
    '      "metes": null | {',
    '        "commencement": null | { "anchor": string, "of": string },',
    '        "commencement_offsets": [{ "bearing": string, "distance_ft": number }],',
    '        "calls": [{ "bearing": string, "distance_ft": number }]',
    '      }',
    '    }',
    '  ]',
    '}',
    '',
    'Rules:',
    ' - If the description names whole sections, use "ALL".',
    ' - Normalize aliquot: "Northeast Quarter" -> "NE/4", "North Half" -> "N/2".',
    ' - Extract state abbrev (CO, WY, IL, KS, ...) from state name.',
    ' - Extract meridian name - "6th Principal Meridian", "6th P.M.",',
    '   "Third Principal Meridian", "Boise Meridian", etc.',
    ' - If the text contains "commencing at" or "beginning at" and a sequence',
    '   of bearings and distances (metes-and-bounds), populate "metes". Otherwise',
    '   set "metes": null.',
    ' - commencement.anchor examples: "SE_corner", "NE_corner", "NW_corner",',
    '   "SW_corner", "center". Default .of = "section".',
    ' - commencement_offsets are calls between the anchor and the Point of Beginning.',
    '   calls are the actual parcel boundary from POB back to POB.',
    ' - Preserve bearings verbatim (e.g. "N 45° 30\' 15\" E"). Distances in feet',
    '   as numbers (e.g. 125.34). If distance is given in chains/rods/links,',
    '   convert to feet (1 chain = 66 ft, 1 rod = 16.5 ft, 1 link = 0.66 ft).',
    ' - Respond with ONLY the JSON.',
    '',
    'Legal description to parse:',
    text,
  ].join('\n');
  const resp = await fetch(CLAUDE_PROXY, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data.error || `Claude error ${resp.status}`);
  const raw = (data.text || '').trim();
  const cleaned = raw.replace(/^```(?:json)?/, '').replace(/```$/, '').trim();
  try { return JSON.parse(cleaned); }
  catch { throw new Error('Claude returned non-JSON: ' + raw.slice(0, 200)); }
}

async function fetchCells(section, layer) {
  const twn = pad(section.township, 3);
  const rng = pad(section.range, 3);
  const sec2 = pad(section.section, 2);
  const sec3 = pad(section.section, 3);
  const clauses = [
    `TWNSHPNO='${twn}'`,
    `TWNSHPDIR='${section.township_dir}'`,
    `RANGENO='${rng}'`,
    `RANGEDIR='${section.range_dir}'`,
  ];
  // Layer 1 (PLSSTownship) doesn't have FRSTDIVNO / section-level fields.
  // Including it on layer 1 causes a 400 "Failed to execute query" from BLM.
  if (layer !== '1') {
    clauses.push(`(FRSTDIVNO='${sec3}' OR FRSTDIVNO='${sec2}')`);
  }
  if (section.state) clauses.push(`STATEABBR='${String(section.state).toUpperCase()}'`);
  const pmCode = meridianToCode(section.meridian);
  if (pmCode) clauses.push(`PRINMERCD='${pmCode}'`);
  const where = clauses.join(' AND ');

  // BLM's ArcGIS occasionally times out or 5xxs under load. Retry once.
  let lastErr = null;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const resp = await fetch(PLSS_PROXY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ where, layer }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) return data.features || [];
      lastErr = new Error(data.error || `PLSS lookup failed: ${resp.status}`);
      if (resp.status < 500 && resp.status !== 504) break; // don't retry client errors
    } catch (e) {
      lastErr = e;
    }
    await new Promise((r) => setTimeout(r, 600));
  }
  throw lastErr || new Error('PLSS lookup failed');
}

function meridianToCode(m) {
  if (!m) return null;
  const n = String(m).toLowerCase()
    .replace(/\bprincipal meridian\b/g, '').replace(/\bp\.?m\.?\b/g, '')
    .replace(/\bmeridian\b/g, '').replace(/\s+/g, ' ').trim();
  if (!n) return null;
  for (const [name, code] of Object.entries(MERIDIAN_CODE_BY_NAME)) {
    if (n === name || n.startsWith(name)) return code;
  }
  if (/^\d{2}$/.test(n)) return n;
  return null;
}

function pad(v, n) { return String(v).padStart(n, '0'); }

// ---- Aliquot expansion ----
export function expandAliquotToCodes(aliquot) {
  if (aliquot === 'ALL' || (Array.isArray(aliquot) && aliquot.includes('ALL'))) return allSixteen();
  if (!Array.isArray(aliquot)) return [];
  const out = new Set();
  for (const part of aliquot) for (const code of expandOne(normalizeAliquot(part))) out.add(code);
  return [...out];
}

function allSixteen() {
  const dirs = ['NE','NW','SE','SW'];
  const out = [];
  for (const q of dirs) for (const qq of dirs) out.push(`${qq}${q}`);
  return out;
}

function expandOne(norm) {
  const m = norm.match(/^([A-Z/0-9]+)\s+OF\s+([A-Z/0-9]+)$/);
  if (m) {
    const outerQuarter = quarterCode(m[2]);
    if (!outerQuarter) return [];
    return halfOrQuarterToList(m[1]).map((q) => `${q}${outerQuarter}`);
  }
  const direct = halfOrQuarterToList(norm);
  if (direct.length === 1) {
    const q = direct[0];
    return ['NE','NW','SE','SW'].map((qq) => `${qq}${q}`);
  }
  if (direct.length === 2) {
    const out = [];
    for (const q of direct) for (const qq of ['NE','NW','SE','SW']) out.push(`${qq}${q}`);
    return out;
  }
  return [];
}

function normalizeAliquot(s) {
  return String(s).toUpperCase().replace(/\s+/g, ' ')
    .replace(/NORTHEAST/g, 'NE').replace(/NORTHWEST/g, 'NW')
    .replace(/SOUTHEAST/g, 'SE').replace(/SOUTHWEST/g, 'SW')
    .replace(/NORTH/g, 'N').replace(/SOUTH/g, 'S')
    .replace(/EAST/g, 'E').replace(/WEST/g, 'W')
    .replace(/QUARTER/g, '/4').replace(/HALF/g, '/2')
    .replace(/1\/4/g, '/4').replace(/1\/2/g, '/2')
    .replace(/\bTHE\b/g, '').replace(/\s+/g, ' ').trim();
}

function halfOrQuarterToList(token) {
  const t = token.replace(/\s+/g, '').toUpperCase();
  if (/^NE\/4$/.test(t)) return ['NE'];
  if (/^NW\/4$/.test(t)) return ['NW'];
  if (/^SE\/4$/.test(t)) return ['SE'];
  if (/^SW\/4$/.test(t)) return ['SW'];
  if (/^N\/2$/.test(t)) return ['NE','NW'];
  if (/^S\/2$/.test(t)) return ['SE','SW'];
  if (/^E\/2$/.test(t)) return ['NE','SE'];
  if (/^W\/2$/.test(t)) return ['NW','SW'];
  return [];
}

function quarterCode(token) {
  const arr = halfOrQuarterToList(token);
  return arr.length === 1 ? arr[0] : null;
}

function filterCellsByCode(cells, wantedCodes) {
  const set = new Set(wantedCodes.map((c) => c.toUpperCase()));
  return cells.filter((f) => {
    const code = String((f.properties || {}).SECDIVNO || '').toUpperCase();
    return code && set.has(code);
  });
}

async function fetchSectionFeature(section) {
  const cells = await fetchCells(section, '3');
  // Prefer section-level feature (SECDIVNO null) as a cleaner reference
  const sectionLevel = cells.find((f) => !(f.properties || {}).SECDIVNO);
  if (sectionLevel) return sectionLevel;
  if (cells.length > 0) return cells[0];
  const tws = await fetchCells(section, '1');
  return tws[0] || null;
}

// Given a section feature and a free-text reference like "NE/4 of Section 11",
// "Southeast Quarter", "SW/4 of NE/4", or "section", return the bbox of the
// referenced sub-region inside the section. Returns null if unrecognized.
function bboxForAliquotRef(sectionFeature, refStr) {
  const bb = sectionFeatureBbox(sectionFeature);
  if (!bb) return null;
  const norm = normalizeAliquot(refStr || '');
  // If there's no aliquot info (e.g. just "Section 11"), return whole section
  if (!norm || /SECTION|TRACT/.test(norm) === false && /[NSEW]\/[24]/.test(norm) === false) {
    if (/SECTION/.test(norm)) return bb;
    // No quarter info -> whole section
    return bb;
  }
  if (/SECTION/.test(norm) && !/\/[24]/.test(norm)) return bb;

  // Find aliquot parts within the string. Support patterns like "NE/4",
  // "SW/4 OF NE/4", "N/2".
  const m = norm.match(/([NSEW]+\/[24])\s+OF\s+([NSEW]+\/[24])/);
  let codes;
  if (m) {
    const outer = halfOrQuarterToList(m[2]);
    const inner = halfOrQuarterToList(m[1]);
    if (outer.length === 1 && inner.length >= 1) {
      codes = inner.map((q) => q + outer[0]);
    }
  } else {
    const mm = norm.match(/([NSEW]+\/[24])/);
    if (mm) {
      const list = halfOrQuarterToList(mm[1]);
      if (list.length === 1) {
        codes = ['NE','NW','SE','SW'].map((qq) => qq + list[0]);
      } else if (list.length === 2) {
        codes = [];
        for (const q of list) for (const qq of ['NE','NW','SE','SW']) codes.push(qq + q);
      }
    }
  }
  if (!codes || codes.length === 0) return bb;

  // Compute the union bbox of the referenced QQ codes relative to the section bbox.
  const [x0, y0, x1, y1] = bb;
  const mx = (x0 + x1) / 2, my = (y0 + y1) / 2;
  // First split: quarter-section bbox by last 2 chars
  const qBbox = (code2) => {
    if (code2 === 'NE') return [mx, my, x1, y1];
    if (code2 === 'NW') return [x0, my, mx, y1];
    if (code2 === 'SE') return [mx, y0, x1, my];
    if (code2 === 'SW') return [x0, y0, mx, my];
    return null;
  };
  const qqBbox = (code4) => {
    const qbb = qBbox(code4.slice(-2));
    if (!qbb) return null;
    const [qx0, qy0, qx1, qy1] = qbb;
    const qmx = (qx0 + qx1) / 2, qmy = (qy0 + qy1) / 2;
    const i = code4.slice(0, 2);
    if (i === 'NE') return [qmx, qmy, qx1, qy1];
    if (i === 'NW') return [qx0, qmy, qmx, qy1];
    if (i === 'SE') return [qmx, qy0, qx1, qmy];
    if (i === 'SW') return [qx0, qy0, qmx, qmy];
    return null;
  };
  let mnX=Infinity,mnY=Infinity,mxX=-Infinity,mxY=-Infinity;
  for (const code of codes) {
    const b = code.length === 2 ? qBbox(code) : qqBbox(code);
    if (!b) continue;
    if (b[0] < mnX) mnX = b[0];
    if (b[1] < mnY) mnY = b[1];
    if (b[2] > mxX) mxX = b[2];
    if (b[3] > mxY) mxY = b[3];
  }
  if (!isFinite(mnX)) return bb;
  return [mnX, mnY, mxX, mxY];
}

function sectionFeatureBbox(f) {
  if (!f || !f.geometry) return null;
  let mnX=Infinity,mnY=Infinity,mxX=-Infinity,mxY=-Infinity;
  const walk = (c) => {
    if (typeof c[0] === 'number') {
      if (c[0]<mnX) mnX=c[0]; if (c[0]>mxX) mxX=c[0];
      if (c[1]<mnY) mnY=c[1]; if (c[1]>mxY) mxY=c[1];
    } else for (const x of c) walk(x);
  };
  walk(f.geometry.coordinates);
  if (!isFinite(mnX)) return null;
  return [mnX, mnY, mxX, mxY];
}

